import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faBriefcase,
  faBuilding,
  faChartLine,
  faCheckCircle,
  faChevronLeft,
  faChevronRight,
  faEye,
  faFilterCircleXmark,
  faList,
  faLocationDot,
  faMagnifyingGlass,
  faShieldHalved,
  faTruck
} from '@fortawesome/free-solid-svg-icons';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { LanguageService } from '../services/language.service';
import {
  CompanyDirectoryEntry,
  CompanyDirectoryPage
} from './company-directory.models';
import { CompanyDirectoryService } from './company-directory.service';

@Component({
  selector: 'app-company-directory',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule
  ],
  templateUrl: './company-directory.component.html',
  styleUrls: ['./company-directory.css']
})
export class CompanyDirectoryComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly service = inject(CompanyDirectoryService);
  private readonly router = inject(Router);
  readonly languageService = inject(LanguageService);

  readonly filtersForm = this.fb.group({
    searchText: ['']
  });

  readonly icons = {
    briefcase: faBriefcase,
    building: faBuilding,
    chart: faChartLine,
    check: faCheckCircle,
    chevronLeft: faChevronLeft,
    chevronRight: faChevronRight,
    eye: faEye,
    filterClear: faFilterCircleXmark,
    list: faList,
    location: faLocationDot,
    search: faMagnifyingGlass,
    shield: faShieldHalved,
    truck: faTruck
  };

  readonly companyTypes = [
    { key: '', labelEn: 'All companies', labelAr: 'جميع الشركات' },
    { key: 'FREIGHT_FORWARDER', labelEn: 'Freight Forwarders', labelAr: 'وكلاء شحن' },
    { key: 'TRANSPORTER', labelEn: 'Transporters', labelAr: 'ناقلون' },
    { key: 'BROKER', labelEn: 'Brokers', labelAr: 'وسطاء' },
    { key: 'SHIPPER', labelEn: 'Shippers', labelAr: 'شاحنون' },
    { key: 'LOGISTICS_SERVICE', labelEn: 'Logistics Services', labelAr: 'خدمات لوجستية' }
  ];

  readonly directoryPage = signal<CompanyDirectoryPage | null>(null);
  readonly loading = signal(true);
  error = '';
  selectedCompanyType = '';
  readonly pageSize = 12;
  private readonly failedLogoIds = new Set<number>();

  ngOnInit(): void {
    this.loadDirectory(0);
  }

  get entries(): CompanyDirectoryEntry[] {
    return this.directoryPage()?.content ?? [];
  }

  get totalCompanies(): number {
    return this.directoryPage()?.totalElements ?? 0;
  }

  get totalPages(): number {
    return this.directoryPage()?.totalPages ?? 0;
  }

  get displayTotalPages(): number {
    return Math.max(this.totalPages, 1);
  }

  get currentPage(): number {
    return this.directoryPage()?.number ?? 0;
  }

  get pageStart(): number {
    if (!this.entries.length) return 0;
    return this.currentPage * this.pageSize + 1;
  }

  get pageEnd(): number {
    if (!this.entries.length) return 0;
    return Math.min(this.pageStart + this.entries.length - 1, this.totalCompanies);
  }

  get canGoPrevious(): boolean {
    return this.currentPage > 0 && !this.loading();
  }

  get canGoNext(): boolean {
    return this.totalPages > 0 && this.currentPage < this.totalPages - 1 && !this.loading();
  }

  get searchSummary(): { label: string; value: string }[] {
    const summary: { label: string; value: string }[] = [];
    const searchText = this.normalizedSearchText();
    if (searchText) {
      summary.push({ label: 'Search', value: searchText });
    }
    if (this.selectedCompanyType) {
      summary.push({ label: 'Type', value: this.companyTypeLabel(this.selectedCompanyType) });
    }
    return summary;
  }

  loadDirectory(page = 0): void {
    this.loading.set(this.entries.length === 0);
    this.error = '';

    const query = this.normalizedSearchText();
    const companyType = this.selectedCompanyType || undefined;

    this.service.searchCompanies({
      query: query || undefined,
      companyType,
      page,
      size: this.pageSize
    }).subscribe({
      next: (directoryPage) => {
        this.directoryPage.set(this.normalizePage(directoryPage, page));
        this.loading.set(false);
      },
      error: () => {
        this.error = this.text(
          'Failed to load company directory.',
          'فشل تحميل دليل الشركات.'
        );
        this.directoryPage.set(null);
        this.loading.set(false);
      }
    });
  }

  search(): void {
    this.loadDirectory(0);
  }

  clearFilters(): void {
    this.filtersForm.reset({ searchText: '' });
    this.selectedCompanyType = '';
    this.loadDirectory(0);
  }

  setTypeFilter(type: string): void {
    this.selectedCompanyType = this.selectedCompanyType === type ? '' : type;
    this.loadDirectory(0);
  }

  previousPage(): void {
    if (this.canGoPrevious) {
      this.loadDirectory(this.currentPage - 1);
    }
  }

  nextPage(): void {
    if (this.canGoNext) {
      this.loadDirectory(this.currentPage + 1);
    }
  }

  viewCompany(id: number): void {
    this.router.navigate(['/directory', id]);
  }

  logoUrl(entry: CompanyDirectoryEntry): string | null {
    const logo = entry.logo?.trim();
    if (!logo || this.failedLogoIds.has(entry.id)) {
      return null;
    }
    return logo;
  }

  logoInitial(entry: CompanyDirectoryEntry): string {
    const name = (entry.name || this.companyTypeLabel(entry.companyType) || 'C').trim();
    return name.charAt(0).toUpperCase();
  }

  markLogoFailed(entry: CompanyDirectoryEntry): void {
    this.failedLogoIds.add(entry.id);
  }

  text(en: string, ar: string): string {
    return this.languageService.currentLanguage() === 'ar' ? ar : en;
  }

  trustScoreClass(score: number | null): string {
    if (score === null || score === undefined) return 'score-none';
    if (score >= 60) return 'score-high';
    if (score >= 30) return 'score-medium';
    return 'score-low';
  }

  scoreLabel(score: number | null): string {
    if (score === null || score === undefined) return '--';
    return score.toString();
  }

  companyTypeLabel(type: string | null): string {
    const found = this.companyTypes.find(t => t.key === type);
    if (!found) return type ?? this.text('Other', 'أخرى');
    return this.text(found.labelEn, found.labelAr);
  }

  formatCompletion(rate: number): string {
    return `${Math.round(rate)}%`;
  }

  formatDate(dateStr: string): string {
    const d = new Date(dateStr);
    return d.toLocaleDateString(
      this.languageService.currentLanguage() === 'ar' ? 'ar-SA' : 'en-US',
      { month: 'short', day: 'numeric' }
    );
  }

  activeLoadsText(count: number): string {
    return count > 0
      ? this.text(`${count} active`, `${count} نشطة`)
      : this.text('No active loads', 'لا توجد شحنات نشطة');
  }

  private normalizedSearchText(): string {
    return (this.filtersForm.controls.searchText.value ?? '').trim();
  }

  private normalizePage(page: CompanyDirectoryPage, requestedPage: number): CompanyDirectoryPage {
    const content = page?.content ?? [];
    const totalElements = page?.totalElements ?? content.length;
    const totalPages = page?.totalPages ?? Math.ceil(totalElements / this.pageSize);
    return {
      ...page,
      content,
      totalElements,
      totalPages,
      size: page?.size ?? this.pageSize,
      number: page?.number ?? requestedPage,
      first: page?.first ?? requestedPage === 0,
      last: page?.last ?? requestedPage >= Math.max(totalPages - 1, 0)
    };
  }
}
